/**
 * API client for backtime-webapp
 *
 * Typed API client with rate limiting, error handling, and auth token management.
 */

import type { Timeline, Schedule, Phase } from '../types';

// ============================================================================
// Configuration
// ============================================================================

/** API base URL from environment variable or default to localhost */
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** Token storage key */
const TOKEN_KEY = 'serviceTimer_authToken';

/** Unique client ID for this session (used for WebSocket echo-back prevention) */
const CLIENT_ID = crypto.randomUUID();

/**
 * Get the unique client ID for this session
 */
export const getClientId = (): string => CLIENT_ID;

// ============================================================================
// Types
// ============================================================================

/** API error with extended properties */
export interface ApiError extends Error {
  code?: string;
  status?: number;
  details?: unknown;
  isOffline?: boolean;
  retryAfter?: number;
  email?: string;
  serverValue?: unknown;
  serverUpdatedAt?: string;
}

/** Request options extending fetch options */
export interface ApiRequestOptions extends RequestInit {
  /** Timestamp for optimistic concurrency control */
  ifUnmodifiedSince?: string;
}

/** Rate limit state */
interface RateLimitState {
  isLimited: boolean;
  retryAfter: number | null;
  resetTime: number | null;
}

/** Rate limit event handler */
type RateLimitHandler = (state: RateLimitState) => void;

// ============================================================================
// Auth Token Management
// ============================================================================

/**
 * Store authentication token
 */
export const setAuthToken = (token: string | null): void => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

/**
 * Get stored authentication token
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove authentication token
 */
export const removeAuthToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// ============================================================================
// Rate Limit Management
// ============================================================================

let rateLimitState: RateLimitState = {
  isLimited: false,
  retryAfter: null,
  resetTime: null,
};

const rateLimitHandlers: Set<RateLimitHandler> = new Set();

/**
 * Subscribe to rate limit state changes
 */
export const onRateLimitChange = (handler: RateLimitHandler): (() => void) => {
  rateLimitHandlers.add(handler);
  // Immediately notify of current state
  handler(rateLimitState);
  // Return unsubscribe function
  return () => rateLimitHandlers.delete(handler);
};

/**
 * Get current rate limit state
 */
export const getRateLimitState = (): RateLimitState => ({ ...rateLimitState });

/**
 * Update rate limit state and notify handlers
 */
const updateRateLimitState = (newState: RateLimitState): void => {
  rateLimitState = newState;
  rateLimitHandlers.forEach((handler) => handler(newState));
};

/**
 * Clear rate limit (called when reset time passes)
 */
const clearRateLimit = (): void => {
  updateRateLimitState({
    isLimited: false,
    retryAfter: null,
    resetTime: null,
  });
};

// ============================================================================
// API Request
// ============================================================================

/**
 * Make authenticated API request with rate limit handling
 */
export const apiRequest = async <T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  // Check if we're currently rate limited
  if (rateLimitState.isLimited && rateLimitState.resetTime) {
    const now = Date.now();
    if (now < rateLimitState.resetTime) {
      const error = new Error('Rate limited - please wait') as ApiError;
      error.code = 'RATE_LIMITED';
      error.status = 429;
      error.retryAfter = Math.ceil((rateLimitState.resetTime - now) / 1000);
      throw error;
    } else {
      // Rate limit has expired
      clearRateLimit();
    }
  }

  const token = getAuthToken();
  const { ifUnmodifiedSince, ...fetchOptions } = options;

  const config: RequestInit = {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': CLIENT_ID,
      ...(fetchOptions.headers as Record<string, string>),
    },
  };

  // Add auth token if available
  if (token) {
    (config.headers as Record<string, string>)['Authorization'] =
      `Bearer ${token}`;
  }

  // Add If-Unmodified-Since header for optimistic concurrency control
  if (ifUnmodifiedSince) {
    (config.headers as Record<string, string>)['If-Unmodified-Since'] =
      ifUnmodifiedSince;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, config);

    // Handle rate limiting (429)
    if (response.status === 429) {
      const retryAfter = parseInt(
        response.headers.get('Retry-After') || '60',
        10
      );
      const resetTime = Date.now() + retryAfter * 1000;

      updateRateLimitState({
        isLimited: true,
        retryAfter,
        resetTime,
      });

      // Schedule automatic clear
      setTimeout(clearRateLimit, retryAfter * 1000);

      const error = new Error(
        `Rate limited. Please try again in ${retryAfter} seconds.`
      ) as ApiError;
      error.code = 'RATE_LIMITED';
      error.status = 429;
      error.retryAfter = retryAfter;
      throw error;
    }

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        const error = new Error(
          `HTTP error! status: ${response.status}`
        ) as ApiError;
        error.status = response.status;
        throw error;
      }
      return null as T;
    }

    const data = await response.json();

    if (!response.ok) {
      // Check for EMAIL_NOT_VERIFIED error
      if (response.status === 403 && data.code === 'EMAIL_NOT_VERIFIED') {
        const error = new Error(
          data.error || 'Email not verified'
        ) as ApiError;
        error.code = 'EMAIL_NOT_VERIFIED';
        error.status = 403;
        error.email = data.email;
        throw error;
      }

      // Check for conflict error
      if (response.status === 409 && data.error === 'conflict') {
        const error = new Error(
          data.message || 'Conflict detected'
        ) as ApiError;
        error.code = 'CONFLICT';
        error.status = 409;
        error.serverValue = data.serverValue;
        error.serverUpdatedAt = data.serverUpdatedAt;
        throw error;
      }

      // Log full error details for debugging
      if (data.details) {
        console.error('Validation error details:', data.details);
      }

      const error = new Error(
        data.error || `HTTP error! status: ${response.status}`
      ) as ApiError;
      error.status = response.status;
      error.details = data.details;
      throw error;
    }

    return data as T;
  } catch (error) {
    const apiError = error as ApiError;

    // Check for network errors
    if (apiError.name === 'TypeError' && apiError.message === 'Failed to fetch') {
      apiError.code = 'NETWORK_ERROR';
      apiError.isOffline = !navigator.onLine;
    }

    console.error('API request failed:', error);
    throw error;
  }
};

// ============================================================================
// API Client
// ============================================================================

/**
 * Typed API client with common HTTP methods
 */
export const api = {
  get: <T = unknown>(endpoint: string): Promise<T> =>
    apiRequest<T>(endpoint, { method: 'GET' }),

  post: <T = unknown>(endpoint: string, data?: unknown): Promise<T> =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: { ifUnmodifiedSince?: string }
  ): Promise<T> =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    }),

  delete: <T = unknown>(endpoint: string, data?: unknown): Promise<T> =>
    apiRequest<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    }),
};

// ============================================================================
// API Response Types
// ============================================================================

/** Timeline response from API */
export interface TimelineResponse {
  timeline: Timeline;
}

/** Timelines list response from API */
export interface TimelinesResponse {
  timelines: Timeline[];
}

/** User from API */
export interface UserResponse {
  id: string;
  email: string;
  displayName: string | null;
  isAdmin?: boolean;
  createdAt: string;
  updatedAt?: string;
}

/** Auth response */
export interface AuthResponse {
  user: UserResponse;
  token: string;
}

/** Time sync response */
export interface TimeSyncResponse {
  serverTime: number;
  timestamp: string;
}

// ============================================================================
// Typed API Methods
// ============================================================================

/**
 * Timeline API methods with proper typing
 */
export const timelineApi = {
  /** Get all timelines for current user */
  getAll: (): Promise<TimelinesResponse> => api.get('/timelines'),

  /** Get a single timeline by ID */
  get: (id: string): Promise<TimelineResponse> => api.get(`/timelines/${id}`),

  /** Get a public timeline by code */
  getPublic: (code: string): Promise<TimelineResponse> =>
    api.get(`/timelines/public/${code}`),

  /** Create a new timeline */
  create: (data: {
    name: string;
    schedule?: Schedule;
    phases?: Phase[];
  }): Promise<TimelineResponse> => api.post('/timelines', data),

  /** Update a timeline */
  update: (
    id: string,
    data: Partial<Timeline>,
    options?: { ifUnmodifiedSince?: string }
  ): Promise<TimelineResponse> => api.put(`/timelines/${id}`, data, options),

  /** Delete a timeline */
  delete: (id: string): Promise<void> => api.delete(`/timelines/${id}`),
};

/**
 * Auth API methods with proper typing
 */
export const authApi = {
  /** Login with email/password */
  login: (email: string, password: string): Promise<AuthResponse> =>
    api.post('/auth/login', { email, password }),

  /** Register new user */
  register: (
    email: string,
    password: string,
    displayName?: string
  ): Promise<AuthResponse> =>
    api.post('/auth/register', { email, password, displayName }),

  /** Login with Google */
  googleLogin: (credential: string): Promise<AuthResponse> =>
    api.post('/auth/google', { credential }),

  /** Login with Apple */
  appleLogin: (authCode: string): Promise<AuthResponse> =>
    api.post('/auth/apple', { authCode }),

  /** Get current user */
  me: (): Promise<UserResponse> => api.get('/auth/me'),

  /** Resend verification email */
  resendVerification: (email: string): Promise<{ message: string }> =>
    api.post('/auth/resend-verification', { email }),
};

/**
 * Time sync API
 */
export const timeApi = {
  /** Get server time for synchronization */
  sync: (): Promise<TimeSyncResponse> => api.get('/time'),
};

export default api;
